import { useState, useRef, useEffect } from 'react';
import { useCategories } from '../../../hooks/useCategories';
import type { Payee } from '../../../hooks/usePayees';

interface Props {
  payees: Payee[];
  value: string | null;
  valueName: string | null;
  onChange: (id: string | null, name: string | null, category?: string | null, subcategory?: string | null) => void;
  onQuickCreate: (name: string, category?: string, subcategory?: string) => Promise<{ success: boolean; data?: Payee; message: string }>;
  /** IDs of recently used payees (from transaction history) */
  recentPayeeIds?: string[];
  disabled?: boolean;
}

/** Compact inline category pills — expense categories only */
const InlineCategoryPills = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const { categories, loading } = useCategories('expense');
  if (loading) return <div className="h-6 w-32 bg-gray-100 rounded-xl animate-pulse" />;
  return (
    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
      {categories.map(cat => {
        const sel = value === cat.name;
        return (
          <button key={cat.id} type="button" onClick={() => onChange(sel ? '' : cat.name)}
            style={sel ? { borderColor: cat.color, backgroundColor: cat.color + '20', color: cat.color } : undefined}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-600 transition-all cursor-pointer whitespace-nowrap ${sel ? '' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            {cat.icon && <i className={`${cat.icon} text-xs`} />}
            {cat.name}
          </button>
        );
      })}
    </div>
  );
};

export const PayeeSelector = ({ payees, value, valueName, onChange, onQuickCreate, recentPayeeIds = [], disabled }: Props) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [inlineCategory, setInlineCategory] = useState('');
  const [inlineSubcategory, setInlineSubcategory] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selected = payees.find(p => p.id === value) ?? null;
  const displayName = selected?.name ?? valueName ?? null;

  const filtered = payees.filter(p => p.is_active && p.name.toLowerCase().includes(search.toLowerCase()));
  const exactMatch = filtered.some(p => p.name.toLowerCase() === search.toLowerCase().trim());
  const showCreateOption = search.trim().length >= 2 && !exactMatch;

  // Recent payees section (only shown when no search)
  const recentPayees = recentPayeeIds
    .map(id => payees.find(p => p.id === id && p.is_active))
    .filter((p): p is Payee => p != null && p.id !== value)
    .slice(0, 4);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setShowInlineCreate(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (p: Payee) => {
    onChange(p.id, p.name, p.default_category, p.default_subcategory);
    setOpen(false);
    setSearch('');
    setShowInlineCreate(false);
  };

  const handleClear = () => {
    onChange(null, null, null, null);
    setSearch('');
  };

  const handleConfirmCreate = async () => {
    if (!search.trim()) return;
    setCreating(true);
    const res = await onQuickCreate(search.trim(), inlineCategory || undefined, inlineSubcategory || undefined);
    setCreating(false);
    if (res.success && res.data) {
      onChange(res.data.id, res.data.name, res.data.default_category, res.data.default_subcategory);
      setOpen(false);
      setSearch('');
      setShowInlineCreate(false);
      setInlineCategory('');
      setInlineSubcategory('');
    }
  };

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen(v => !v); }}
        className={`w-full flex items-center gap-2 px-3 py-2.5 border rounded-xl text-sm text-left transition-all cursor-pointer
          ${open ? 'border-emerald-400 ring-2 ring-emerald-50' : 'border-gray-200 hover:border-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <i className="ri-user-heart-line text-gray-400 shrink-0" />
        {displayName ? (
          <span className="flex-1 text-gray-800 font-500 truncate">{displayName}</span>
        ) : (
          <span className="flex-1 text-gray-400">Selecionar ou digitar nome...</span>
        )}
        {selected?.default_category && !open && (
          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full shrink-0 hidden sm:inline">
            {selected.default_category}
          </span>
        )}
        {displayName ? (
          <button type="button" onClick={e => { e.stopPropagation(); handleClear(); }}
            className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 cursor-pointer shrink-0">
            <i className="ri-close-line text-xs" />
          </button>
        ) : (
          <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-gray-400 shrink-0`} />
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl z-50 overflow-hidden" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
              <input
                autoFocus
                value={search}
                onChange={e => { setSearch(e.target.value); setShowInlineCreate(false); }}
                placeholder="Buscar ou digitar nome..."
                className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {/* Recentes section (only when no search) */}
            {!search.trim() && recentPayees.length > 0 && (
              <div>
                <div className="px-3 pt-2 pb-1">
                  <span className="text-[10px] font-700 text-gray-400 uppercase tracking-wide">Recentes</span>
                </div>
                <div className="flex flex-wrap gap-1.5 px-3 pb-2">
                  {recentPayees.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleSelect(p)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-600 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${p.type === 'pf' ? 'ri-user-line' : 'ri-building-line'} text-xs`} />
                      {p.name}
                    </button>
                  ))}
                </div>
                {filtered.length > 0 && <div className="border-t border-gray-100 mx-3 mb-1" />}
              </div>
            )}

            {/* Todos os favorecidos label */}
            {!search.trim() && filtered.length > 0 && (
              <div className="px-3 pt-1 pb-1">
                <span className="text-[10px] font-700 text-gray-400 uppercase tracking-wide">Todos</span>
              </div>
            )}

            {/* Empty state */}
            {filtered.length === 0 && !showCreateOption && (
              <p className="text-xs text-gray-400 text-center py-4">
                {search ? 'Nenhum favorecido encontrado' : 'Nenhum favorecido cadastrado'}
              </p>
            )}

            {/* Payee list */}
            {filtered.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSelect(p)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors cursor-pointer ${p.id === value ? 'bg-emerald-50' : ''}`}
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-100 shrink-0">
                  <i className={`${p.type === 'pf' ? 'ri-user-line' : 'ri-building-line'} text-emerald-600 text-xs`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-600 text-gray-800 truncate">{p.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {p.nickname && <span className="text-[11px] text-gray-400 truncate">{p.nickname}</span>}
                    {p.default_category && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-600 shrink-0">
                        {p.default_category}
                      </span>
                    )}
                  </div>
                </div>
                {p.id === value && <i className="ri-check-line text-emerald-500 shrink-0" />}
              </button>
            ))}

            {/* Inline create option */}
            {showCreateOption && !showInlineCreate && (
              <button
                type="button"
                onClick={() => setShowInlineCreate(true)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-emerald-50 transition-colors cursor-pointer border-t border-gray-100"
              >
                <i className="ri-add-circle-line text-emerald-500 shrink-0" />
                <span className="text-sm font-600 text-emerald-700">Criar &quot;{search.trim()}&quot;</span>
                <span className="ml-auto text-[10px] text-gray-400">+ categoria opcional</span>
              </button>
            )}
          </div>

          {/* Inline create form */}
          {showInlineCreate && (
            <div className="border-t border-gray-100 p-3 space-y-3 bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-100 shrink-0">
                  <i className="ri-add-line text-emerald-600 text-xs" />
                </div>
                <div>
                  <p className="text-sm font-700 text-gray-800">Criar &quot;{search.trim()}&quot;</p>
                  <p className="text-[11px] text-gray-400">Defina uma categoria padrão (opcional)</p>
                </div>
              </div>
              <InlineCategoryPills value={inlineCategory} onChange={setInlineCategory} />
              {inlineCategory && (
                <input
                  value={inlineSubcategory}
                  onChange={e => setInlineSubcategory(e.target.value)}
                  placeholder="Subcategoria (opcional)..."
                  className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-emerald-400 bg-white"
                />
              )}
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowInlineCreate(false)}
                  className="flex-1 py-2 border border-gray-200 text-gray-500 text-xs font-600 rounded-lg hover:bg-white cursor-pointer whitespace-nowrap">
                  Voltar
                </button>
                <button type="button" onClick={handleConfirmCreate} disabled={creating}
                  className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-xs font-600 rounded-lg cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
                  {creating && <i className="ri-loader-4-line animate-spin" />}
                  {creating ? 'Criando...' : 'Criar e selecionar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
